import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api/axios';

const ConfigContext = createContext();

const DEFAULT_CONFIG = {
    primaryColor: '',
    secondaryColor: '',
    backgroundColor: '',
    globalFontColor: '',
    logoUrl: '',
    buttonStyles: { borderRadius: '', padding: '' },
    pages: {},
    pageDesigns: {},
    event_letters: {},
    registration_fields: {},
    fontFamily: '',
    secondaryFontFamily: '',
    tertiaryFontFamily: '',
    backgroundImage: '',
    backgroundMobile: '',
    backgroundDesktop: '',
    elementShape: ''
};

export const ConfigProvider = ({ children }) => {
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const t = Date.now();
                const [visualRes, pagesRes, customRes, designsRes, lettersRes, regRes] = await Promise.all([
                    api.get(`/configs/visual_identity?_t=${t}`),
                    api.get(`/configs/page_controls?_t=${t}`),
                    api.get(`/configs/custom_gallery_pages?_t=${t}`),
                    api.get(`/configs/page_designs?_t=${t}`),
                    api.get(`/configs/event_letters?_t=${t}`),
                    api.get(`/configs/registration_fields?_t=${t}`)
                ]);
                
                const newConfig = {
                    ...(visualRes.data || {}),
                    pages: pagesRes.data || {},
                    pageControls: pagesRes.data || {},
                    customGalleryPages: Array.isArray(customRes.data) ? customRes.data : [],
                    pageDesigns: designsRes.data || {},
                    event_letters: lettersRes.data || {},
                    registration_fields: regRes.data || {}
                };
                
                // Inject fonts to DOM
                if (newConfig.fontFamily) {
                    document.documentElement.style.setProperty('--main-font', `"${newConfig.fontFamily}", sans-serif`);
                } else {
                    document.documentElement.style.setProperty('--main-font', 'inherit');
                }
                if (newConfig.secondaryFontFamily) {
                    document.documentElement.style.setProperty('--secondary-font', `"${newConfig.secondaryFontFamily}", sans-serif`);
                } else {
                    document.documentElement.style.setProperty('--secondary-font', 'inherit');
                }
                if (newConfig.tertiaryFontFamily) {
                    document.documentElement.style.setProperty('--tertiary-font', `"${newConfig.tertiaryFontFamily}", sans-serif`);
                } else {
                    document.documentElement.style.setProperty('--tertiary-font', 'inherit');
                }

                // Inject colors to DOM
                if (newConfig.primaryColor) {
                    document.documentElement.style.setProperty('--primary-purple', newConfig.primaryColor);
                } else {
                    document.documentElement.style.setProperty('--primary-purple', '#9d4edd');
                }
                if (newConfig.backgroundColor) {
                    document.documentElement.style.setProperty('--bg-deep-purple', newConfig.backgroundColor);
                } else {
                    document.documentElement.style.setProperty('--bg-deep-purple', '#1c0042');
                }
                if (newConfig.globalFontColor) {
                    document.documentElement.style.setProperty('--global-font-color', newConfig.globalFontColor);
                } else {
                    document.documentElement.style.setProperty('--global-font-color', '#000000');
                }

                setConfig(prev => JSON.stringify(prev) === JSON.stringify(newConfig) ? prev : newConfig);
            } catch (error) {
                console.error("Config fetch failed, using defaults", error);
            } finally {
                setLoading(false);
            }
        };

        fetchConfig();
        const interval = setInterval(fetchConfig, 1000);
        return () => clearInterval(interval);
    }, []);

    return (
        <ConfigContext.Provider value={{ config, loading }}>
            {children}
        </ConfigContext.Provider>
    );
};

export const useConfig = () => useContext(ConfigContext);
